import { Injectable } from "@angular/core";
import { ID } from "@common/interfaces/id";
import { IIssue } from "@common/interfaces/issue";
import { IIssueState } from "@common/interfaces/issueState";
import { IIssueType } from "@common/interfaces/issueType";
import { InterfaceNameValue } from "@common/interfaces/issueTypeInterface";
import { WorkflowDog } from "@common/utils/workflowDog";
import { combineQueries } from "@datorama/akita";
import { combineLatest, Observable, of } from "rxjs";
import { filter, map, switchMap, tap } from "rxjs/operators";
import { AuthQuery } from "../auth/auth.query";
import { IssueContextQuery } from "./issueContextQuery";
import { IssueStateQuery } from "./issueState/issueState.query";
import { IssueStateWorkflowQuery } from "./issueStateWorkflow/issueStateWorkflow.query";
import { IssueTypeQuery } from "./issueType/issueType.query";
import { IssueTypeInterfaceQuery } from "./issueTypeInterface/issueTypeInterface.query";
import { IssueTypeWorkflowQuery } from "./issueTypeWorkflow/issueTypeWorkflow.query";
import { ProjectQuery } from "./project/project.query";
import { UserQuery } from "./user/user.query";
import { WorkflowQuery } from "./workflow/workflow.query";
import { ITaskTypeCategory } from "@common/interfaces/issueTypeWorflow";
import { WithRequired } from "@common/utils/type.helpers";
import { wfEvent } from "@common/interfaces/notificationMail";
import { TranslateService } from "@ngx-translate/core";
import { UserApiService } from "./user/userApi.service";

/** One (project × task type × interface) association, used by the reusable search interface picker
 *  to narrow the interface list by project and task type in both directions, client-side. */
export interface InterfacesForProjectsItem {
  projectId: ID;
  issueTypeId: ID;
  taskType: string;
  interfaceName: InterfaceNameValue;
  typeName: string;
}

@Injectable({
  providedIn: 'root'
})
export class WorkflowDependentQuery {
  constructor(
    private workflowQuery: WorkflowQuery,
    private issueContextQuery: IssueContextQuery,
    private issueTypeWorkflowQuery: IssueTypeWorkflowQuery,
    private issueTypeQuery: IssueTypeQuery,
    private issueStateQuery: IssueStateQuery,
    private projectQuery: ProjectQuery,
    private authQuery: AuthQuery,
    private issueStateWorkflowQuery: IssueStateWorkflowQuery,
    private issueTypeInterfaceQuery: IssueTypeInterfaceQuery,
    private userQuery: UserQuery,
    private userApiService: UserApiService,
    private _translateService: TranslateService,
  ) {}

  selectAllowedTypesValidForCreationByWorkflow$(workflowId: ID): Observable<IIssueType[]> {
    return combineLatest([this.issueTypeQuery.all$, this.issueTypeWorkflowQuery.selectValidForCreationByWorkflow$(workflowId)]).pipe(
      map(([types, allowed]) => allowed.map((entity) => types.find((type) => type.id === entity.issueTypeId)))
    );
  }

  selectAllowedTypesValidForCreationByWorkflowAndCategoryId$(workflowId: ID, categoryId: ID): Observable<IIssueType[]> {
    return this.issueTypeWorkflowQuery.selectValidForCreationByWorkflow$(workflowId).pipe(
      switchMap((issueTypeWorkflow) =>
        this.issueTypeQuery.selectMany(issueTypeWorkflow.filter((itw) => itw.categoryId === categoryId).map((itw) => itw.issueTypeId))
      ),
      filter((tw) => !!tw)
    );
  }

  selectAllowedTypesByWorkflowAndCategoryId$(workflowId: ID, categoryId: ID): Observable<IIssueType[]> {
    return this.issueTypeWorkflowQuery.selectByWorkflow$(workflowId).pipe(
      switchMap(issueTypeWorkflow => this.issueTypeQuery.selectMany(issueTypeWorkflow.filter(itw => itw.categoryId === categoryId).map(itw => itw.issueTypeId))),
      filter(tw => !!tw),
    )
  }

  selectAllowedTypesValidForCreationByWorkflowAndCategories$(
    workflowId: ID,
    categories: WithRequired<ITaskTypeCategory, 'id'>[]
  ): Observable<IIssueType[]> {
    return this.issueTypeWorkflowQuery.selectValidForCreationByWorkflow$(workflowId).pipe(
      map((types) => types.filter((entity) => categories.some((category) => category.id === entity.categoryId))),
      switchMap((allowedTypes) => this.issueTypeQuery.selectMany(allowedTypes.map((type) => type.issueTypeId)))
    );
  }

  selectAllowedTypesValidForCreation$(): Observable<IIssueType[]> {
    return combineLatest([this.issueTypeQuery.all$, this.issueTypeWorkflowQuery.selectValidForCreation$()]).pipe(
      map(([types, allowed]) => allowed.map((entity) => types.find((type) => type.id === entity.issueTypeId)))
    );
  }

  getAllowedTypesValidForCreationByWorkflow(workflowId: ID): IIssueType[] {
    const validIssueType = this.issueTypeWorkflowQuery.getValidForCreationByWorkflow(workflowId);
    return validIssueType.map((allowed) => this.issueTypeQuery.getEntity(allowed.issueTypeId));
  }

  selectAllowedTypesByWorkflow$(workflowId: ID): Observable<IIssueType[]> {
    return combineLatest([this.issueTypeQuery.all$, this.issueTypeWorkflowQuery.selectByWorkflow$(workflowId)]).pipe(
      map(([types, allowed]) => allowed.map((entity) => types.find((type) => type.id === entity.issueTypeId)))
    );
  }

  selectAllowedStatesByWorkflow$(workflowId: ID): Observable<IIssueState[]> {
    return combineLatest([this.issueStateQuery.all$, this.issueStateWorkflowQuery.selectByWorkflow$(workflowId)]).pipe(
      map(([states, allowed]) => allowed.map((entity) => states.find((state) => state.id === entity.issueStateId)))
    );
  }

  getWorkflowsWithIssueType(issueTypeId: ID): ID[] {
    return [...new Set(this.issueTypeWorkflowQuery.getByIssueType(issueTypeId).map((c) => c.workflowId))];
  }

  getAllowedTypesByWorkflow(id: ID): IIssueType[] {
    const issueTypes = this.issueTypeQuery.getAll();
    const allowedTypes = this.issueTypeWorkflowQuery.getAll({ filterBy: (item) => item.workflowId === id });
    return allowedTypes.map((allowed) => issueTypes.find((issueType) => issueType.id === allowed.issueTypeId));
  }

  getAllowedStatesByWorkflow(id: ID): IIssueState[] {
    const issueStates = this.issueStateQuery.getAll();
    const allowedStates = this.issueStateWorkflowQuery.getAll({ filterBy: (item) => item.workflowId === id });
    return allowedStates.map((allowed) => issueStates.find((issueState) => issueState.id === allowed.issueStateId));
  }

  getInterfaceNameByWorkflow(id: ID): InterfaceNameValue[] {
    const allowedTypes = this.issueTypeWorkflowQuery.getAll({ filterBy: (item) => item.workflowId === id });
    const interfaceNames = this.issueTypeInterfaceQuery
      .getAll()
      .filter((iti) => allowedTypes.map((at) => at.issueTypeId).includes(iti.issueTypeId))
      .map((i) => i.interfaceName);
    return interfaceNames.filter((value, index, arr) => arr.indexOf(value) === index);
  }

  getInterfacesNameTypeNameByWorkflow(id: ID): { interfaceName: InterfaceNameValue, typeName: string, taskType: string, issueTypeId: string }[] {
    const allowedTypes = this.issueTypeWorkflowQuery.getAll({ filterBy: item => item.workflowId === id });
    const issueTypeInterface = this.issueTypeInterfaceQuery.getAll();
    return issueTypeInterface.reduce((acc: { interfaceName: InterfaceNameValue, typeName: string, taskType: string, issueTypeId: string }[], iti) => {
      if (allowedTypes.some(at => at.issueTypeId === iti.issueTypeId)) {
        acc.push({
          interfaceName: iti.interfaceName,
          typeName: iti.name,
          taskType: iti.id,
          issueTypeId: allowedTypes.find(at => at.issueTypeId === iti.issueTypeId)?.issueTypeId,
        })
      }
      return acc;
    }, [])
      .sort((a, b) => {
        const differentTaskType = a.taskType.localeCompare(b.taskType);
        if (differentTaskType === 0) {
          return a.interfaceName.localeCompare(b.interfaceName);
        } else {
          return differentTaskType;
        }
      });
  }

  selectInterfaceNameByWorkflow$(id: ID): Observable<InterfaceNameValue[]> {
    return combineLatest([
      this.issueTypeWorkflowQuery.selectAll({ filterBy: (item) => item.workflowId === id }),
      this.issueTypeInterfaceQuery.selectAll()
    ]).pipe(
      map(([allowedTypes, issueTypeInterface]) => {
        return issueTypeInterface.filter((iti) => allowedTypes.map((at) => at.issueTypeId).includes(iti.issueTypeId)).map((i) => i.interfaceName);
      })
    );
  }

  selectInterfacesNameTypeNameByWorkflow$(id: ID): Observable<{ interfaceName: InterfaceNameValue; typeName: string; taskType: string }[]> {
    return combineLatest([
      this.issueTypeWorkflowQuery.selectAll({ filterBy: (item) => item.workflowId === id }),
      this.issueTypeInterfaceQuery.selectAll()
    ]).pipe(
      map(([allowedTypes, issueTypeInterface]) => {
        return issueTypeInterface
          .reduce((acc: { interfaceName: InterfaceNameValue; typeName: string; taskType: string }[], iti) => {
            if (allowedTypes.some((at) => at.issueTypeId === iti.issueTypeId)) {
              acc.push({
                interfaceName: iti.interfaceName,
                typeName: iti.name,
                taskType: iti.id
              });
            }
            return acc;
          }, [])
          .sort((a, b) => {
            const differentTaskType = a.taskType.localeCompare(b.taskType);
            if (differentTaskType === 0) {
              return a.interfaceName.localeCompare(b.interfaceName);
            } else {
              return differentTaskType;
            }
          });
      })
    );
  }

  /** Interfaces available across a set of projects, each tagged with its project and task type so the
   *  picker can narrow the list by project and task type (and vice-versa) client-side. Spans several
   *  workflows, unlike the per-workflow selectors above. */
  selectInterfacesForProjects$(projectIds: ID[]): Observable<InterfacesForProjectsItem[]> {
    return combineLatest([
      this.projectQuery.selectAll(),
      this.issueTypeWorkflowQuery.selectAll(),
      this.issueTypeInterfaceQuery.selectAll()
    ]).pipe(
      map(([projects, issueTypeWorkflows, issueTypeInterfaces]) => {
        const scopedProjects = projects.filter((project) => projectIds.includes(project.id));
        const items: InterfacesForProjectsItem[] = [];
        for (const project of scopedProjects) {
          const allowedTypeIds = issueTypeWorkflows
            .filter((itw) => itw.workflowId === project.workflowId)
            .map((itw) => itw.issueTypeId);
          for (const iti of issueTypeInterfaces) {
            if (!allowedTypeIds.includes(iti.issueTypeId)) {
              continue;
            }
            items.push({
              projectId: project.id,
              issueTypeId: iti.issueTypeId,
              taskType: iti.id,
              interfaceName: iti.interfaceName,
              typeName: iti.name
            });
          }
        }
        return items;
      })
    );
  }

  selectAllowedStatesByIssue(issue: IIssue): Observable<IIssueState[]> {
    const issueContext$ = this.issueContextQuery.selectIssueContext$(issue.id);
    return this.projectQuery.selectEntity(issue.projectId).pipe(
      filter((project) => !!project?.workflowId),
      switchMap((project) => combineQueries([
        this.issueStateQuery.selectByWorkflow$(project.workflowId),
        issueContext$,
        this.workflowQuery.selectWorkflowSettingsByWorkflowId$(project.workflowId),
        this.userQuery.selectCurrentUserWithRolesAndOloNetworksByIdAndProject$(issue.projectId),
      ])),
      map(([issueStates, issueContext, workflowSettings, user]) =>
        issueStates.filter((issueState) =>
          WorkflowDog.canChangeState({
            settings: workflowSettings,
            issueContext,
            newStateId: issueState.id,
            user,
          })
        )
      )
    );
  }

  getEventNotificationOptions(): { label: string; value: string }[] {
    const options: {label: string, value: string}[] = [];    
    for (const key of Object.keys(wfEvent)) {
      const translationLabel = 'SETTINGS.NOTIFICATION_MAIL_SETTINGS.EVENTS.' + wfEvent[key].toUpperCase()
      options.push({
        label: this._translateService.instant(translationLabel), 
        value: wfEvent[key]
      });
    }    
    return options;
  }
}
